/**
 * Admin -- self-issued + Stripe-synced invoices.
 *
 * Concierge-sale path: the user generates a Praxara-numbered PDF invoice for
 * one-off Compliance Pack / Snapshot deals that don't go through Stripe
 * Checkout. Stripe-synced rows appear here too (read-only) so the admin has
 * one inbox for everything.
 *
 * Workflow:
 *   1. Click + New invoice -> fill modal -> server mints INV-YYYY-NNNN +
 *      persists row + returns it
 *   2. Click Download PDF on any self-issued row -> streams the PDF
 *   3. Click Mark paid when payment lands (manual today; future Stripe webhook
 *      can flip this automatically for synced rows)
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, FileText, Download, CheckCircle2, Trash2, ExternalLink } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuthStore } from '@/stores/auth';
import { formatDate } from '@/lib/utils';
import { RoleGateCard } from './AdminPromptVersions';

type Currency = 'GBP' | 'EUR' | 'USD';

interface LineItem {
  description: string;
  quantity: number;
  /** Minor units (pence). */
  unitAmount: number;
  total: number;
}

interface Invoice {
  id: string;
  source: 'self_issued' | 'stripe';
  praxaraNumber: string | null;
  number: string | null;
  currency: Currency;
  subtotal: number;
  tax: number;
  total: number;
  amountPaid: number;
  amountDue: number;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
  dueDate: string | null;
  paidAt: string | null;
  billingName: string | null;
  billingEmail: string | null;
  hostedInvoiceUrl: string | null;
  pdfUrl: string | null;
  createdAt: string;
}

const CURRENCY_SYMBOL: Record<Currency, string> = { GBP: '£', EUR: '€', USD: '$' };

// Pre-canned line items the admin can pick to fill the form fast.
const PRESETS: Array<{ label: string; lineItem: LineItem }> = [
  {
    label: 'AI Readiness Snapshot (£2,500)',
    lineItem: { description: 'AI Readiness Snapshot -- pharma compliance gap report', quantity: 1, unitAmount: 250_000, total: 250_000 },
  },
  {
    label: 'Compliance Pack Starter (£5,000)',
    lineItem: { description: 'Compliance Pack Starter -- 8 artefacts, 5-day delivery', quantity: 1, unitAmount: 500_000, total: 500_000 },
  },
  {
    label: 'Compliance Pack Full (£30,000/yr)',
    lineItem: { description: 'Compliance Pack Full -- annual subscription, quarterly refresh', quantity: 1, unitAmount: 3_000_000, total: 3_000_000 },
  },
  {
    label: 'Platform Pilot (£3,000/mo)',
    lineItem: { description: 'Platform Pilot -- 50-seat tenant, one workflow, monthly', quantity: 1, unitAmount: 300_000, total: 300_000 },
  },
];

function fmt(minor: number, currency: Currency): string {
  return `${CURRENCY_SYMBOL[currency]}${(minor / 100).toFixed(2)}`;
}

export function AdminInvoicesPage() {
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'ADMIN';

  const list = useQuery({
    queryKey: ['admin', 'invoices'],
    queryFn: async () => (await api.get<{ items: Invoice[] }>('/api/admin/invoices')).data,
    enabled: isAdmin,
    retry: false,
  });

  const [createOpen, setCreateOpen] = useState(false);

  if (!isAdmin) {
    return <RoleGateCard requiredRole="ADMIN" surface="Invoices" />;
  }

  if (list.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-72" />
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
      </div>
    );
  }

  if (list.isError) {
    return <RoleGateCard requiredRole="ADMIN" surface="Invoices" detail={extractApiError(list.error).message} />;
  }

  const items = list.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl">Invoices</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Self-issued PDF invoices for concierge sales (Compliance Pack + Snapshot) plus
            Stripe-synced invoices from the recurring SaaS billing path.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" /> New invoice
        </Button>
      </div>

      {items.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">
              No invoices yet. Click <strong>New invoice</strong> to create one for a concierge sale.
            </p>
          </CardContent>
        </Card>
      )}

      {items.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">All invoices ({items.length})</CardTitle>
            <CardDescription>Most recent first. Self-issued = Praxara-issued PDF; Stripe-synced = mirrored from billing portal.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-1 divide-y">
              {items.map((inv) => <InvoiceRow key={inv.id} invoice={inv} />)}
            </div>
          </CardContent>
        </Card>
      )}

      {createOpen && <CreateInvoiceDialog onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

function InvoiceRow({ invoice }: { invoice: Invoice }) {
  const qc = useQueryClient();
  const markPaid = useMutation({
    mutationFn: async () => (await api.post<Invoice>(`/api/admin/invoices/${invoice.id}/mark-paid`)).data,
    onSuccess: () => {
      toast.success('Invoice marked paid');
      void qc.invalidateQueries({ queryKey: ['admin', 'invoices'] });
    },
    onError: (err) => toast.error('Failed to mark paid', extractApiError(err).message),
  });

  const statusVariant = invoice.status === 'paid'
    ? 'success'
    : invoice.status === 'open'
    ? 'warning'
    : invoice.status === 'void' || invoice.status === 'uncollectible'
    ? 'destructive'
    : 'secondary';

  const ref = invoice.praxaraNumber ?? invoice.number ?? invoice.id.slice(0, 8);
  const overdue = invoice.status === 'open' && invoice.dueDate && new Date(invoice.dueDate).getTime() < Date.now();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 p-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-medium">{ref}</span>
          <Badge variant={statusVariant}>{invoice.status}</Badge>
          {invoice.source === 'stripe' && <Badge variant="info">stripe</Badge>}
          {overdue && <Badge variant="destructive">overdue</Badge>}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          {invoice.billingName ?? 'Tenant'} · Issued {formatDate(invoice.createdAt)}
          {invoice.dueDate && ` · Due ${formatDate(invoice.dueDate)}`}
          {invoice.paidAt && ` · Paid ${formatDate(invoice.paidAt)}`}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-display text-base">{fmt(invoice.total, invoice.currency)}</span>
        {invoice.source === 'self_issued' && (
          <Button asChild size="sm" variant="outline">
            <a
              href={`/api/admin/invoices/${invoice.id}/pdf`}
              target="_blank"
              rel="noreferrer"
              download={`${ref}.pdf`}
            >
              <Download className="mr-1 h-3 w-3" /> PDF
            </a>
          </Button>
        )}
        {invoice.source === 'stripe' && invoice.hostedInvoiceUrl && (
          <Button asChild size="sm" variant="outline">
            <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-1 h-3 w-3" /> Stripe
            </a>
          </Button>
        )}
        {invoice.status === 'open' && (
          <Button size="sm" onClick={() => markPaid.mutate()} disabled={markPaid.isPending}>
            <CheckCircle2 className="mr-1 h-3 w-3" /> Mark paid
          </Button>
        )}
      </div>
    </div>
  );
}

function CreateInvoiceDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [currency, setCurrency] = useState<Currency>('GBP');
  const [billingName, setBillingName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [dueInDays, setDueInDays] = useState(30);
  const [tax, setTax] = useState(0); // pounds
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { description: '', quantity: 1, unitAmount: 0, total: 0 },
  ]);

  const setLine = (i: number, patch: Partial<LineItem>) =>
    setLineItems((prev) =>
      prev.map((li, idx) => {
        if (idx !== i) return li;
        const next = { ...li, ...patch };
        next.total = next.quantity * next.unitAmount;
        return next;
      }),
    );

  const subtotal = lineItems.reduce((acc, li) => acc + li.total, 0);
  const taxMinor = Math.round(tax * 100);
  const total = subtotal + taxMinor;

  const create = useMutation({
    mutationFn: async () =>
      (await api.post<Invoice>('/api/admin/invoices', {
        currency,
        lineItems: lineItems.filter((li) => li.description.trim().length > 0 && li.total > 0),
        billingName: billingName || undefined,
        billingEmail: billingEmail || undefined,
        billingAddress: billingAddress.trim() ? { line1: billingAddress.trim() } : undefined,
        dueInDays,
        tax: taxMinor || undefined,
        notes: notes || undefined,
      })).data,
    onSuccess: (inv) => {
      toast.success('Invoice created', `${inv.praxaraNumber} for ${fmt(inv.total, inv.currency)}`);
      void qc.invalidateQueries({ queryKey: ['admin', 'invoices'] });
      onClose();
      // Auto-open the PDF for the user.
      window.open(`/api/admin/invoices/${inv.id}/pdf`, '_blank');
    },
    onError: (err) => toast.error('Create failed', extractApiError(err).message),
  });

  const valid = lineItems.some((li) => li.description.trim().length > 0 && li.total > 0);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New invoice</DialogTitle>
          <DialogDescription>
            Praxara-issued PDF invoice. The number is minted on save (INV-YYYY-NNNN). Use a preset
            or fill custom line items.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Presets */}
          <div>
            <Label>Quick presets</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <Button
                  key={p.label}
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setLineItems([{ ...p.lineItem }])}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Bill to */}
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <Label>Customer name</Label>
              <Input value={billingName} onChange={(e) => setBillingName(e.target.value)} placeholder="Acme Pharma Ltd" />
            </div>
            <div>
              <Label>Customer email</Label>
              <Input type="email" value={billingEmail} onChange={(e) => setBillingEmail(e.target.value)} placeholder="ap@acme.com" />
            </div>
            <div className="md:col-span-2">
              <Label>Billing address (one-line)</Label>
              <Input value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} placeholder="10 Bedford Square, London WC1B 3RA, UK" />
            </div>
          </div>

          {/* Currency + Due */}
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <Label>Currency</Label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value as Currency)}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="GBP">GBP £</option>
                <option value="EUR">EUR €</option>
                <option value="USD">USD $</option>
              </select>
            </div>
            <div>
              <Label>Due in (days)</Label>
              <Input type="number" min={1} max={180} value={dueInDays} onChange={(e) => setDueInDays(Number(e.target.value) || 30)} />
            </div>
            <div>
              <Label>VAT ({CURRENCY_SYMBOL[currency]})</Label>
              <Input type="number" min={0} step="0.01" value={tax} onChange={(e) => setTax(Number(e.target.value) || 0)} />
            </div>
          </div>

          {/* Line items */}
          <div>
            <Label>Line items</Label>
            <div className="mt-1 space-y-2">
              {lineItems.map((li, i) => (
                <div key={i} className="grid grid-cols-[1fr_60px_90px_90px_28px] items-center gap-2 rounded-md border p-2">
                  <Input
                    value={li.description}
                    onChange={(e) => setLine(i, { description: e.target.value })}
                    placeholder="Description"
                  />
                  <Input
                    type="number"
                    min={1}
                    value={li.quantity}
                    onChange={(e) => setLine(i, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                  />
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={li.unitAmount / 100}
                    onChange={(e) => setLine(i, { unitAmount: Math.round((Number(e.target.value) || 0) * 100) })}
                    placeholder="Unit (£)"
                  />
                  <div className="text-right text-xs font-mono text-muted-foreground">{fmt(li.total, currency)}</div>
                  <button
                    type="button"
                    onClick={() => setLineItems((prev) => prev.filter((_, idx) => idx !== i))}
                    disabled={lineItems.length <= 1}
                    className="text-muted-foreground hover:text-destructive disabled:opacity-30"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setLineItems((prev) => [...prev, { description: '', quantity: 1, unitAmount: 0, total: 0 }])}
              >
                <Plus className="mr-1 h-3 w-3" /> Add line
              </Button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label>Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="PO #, project ref, etc." />
          </div>

          {/* Totals */}
          <div className="flex items-center justify-between rounded-md bg-muted/40 p-3 text-sm">
            <div className="text-muted-foreground">Subtotal {fmt(subtotal, currency)} · VAT {fmt(taxMinor, currency)}</div>
            <div className="font-display text-lg">Total {fmt(total, currency)}</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!valid || create.isPending}>
            {create.isPending ? 'Creating...' : 'Create invoice + open PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
